/**
 * The wire twins of the BMS domain errors, and the one helper that declares
 * them on a group.
 *
 * BROWSER-SAFE. Every BMS contract imports this to call `withBmsErrors`, and
 * the browser imports the contract, so the public-api import is RELATIVE
 * rather than through that context's barrel (its barrel re-exports
 * repositories that reach `@effect/sql-pg`).
 *
 * BOTH HALVES OR NEITHER. `makePublicApiBoundaryLayer` passes a tag through to
 * the platform's encode stage only if the tag is listed in
 * `additionalEnvelopeErrorTags`, and the platform can only render it if the
 * CONTRACT declared its wire schema with `.addError(...)`. A tag listed in one
 * place and not the other produces an EMPTY response, not an envelope. That is
 * why the list and the declaration are generated from ONE array here, and why
 * a test asserts they are the same set.
 *
 * DISTINCT TAGS FROM THE DOMAIN ERRORS on purpose. `BmsApiNotFoundError` is
 * not `BmsNotFoundError`. If they shared a tag, a domain error that escaped a
 * handler unmapped would be recognised by the boundary as a declared envelope
 * error and rendered from a schema it does not match, which is how a `cause`
 * carrying a Postgres detail line reaches a browser.
 *
 * WHY `region_unset` IS A 409 AND NOT A 422. The public error code catalogue
 * is FROZEN and append-only (`PUBLIC_API_ERROR_CODES`), and has no 422. That
 * is not a workaround: the state being reported is that the firm's own
 * configuration is not ready for the operation, which is the `conflict`
 * semantic, not "your request was malformed". The caller's request was fine.
 */
import type { HttpApiEndpoint, HttpApiGroup } from "@effect/platform"

// RELATIVE, not the public-api barrel. See the header.
import {
  ApiInsufficientScopeWire,
  ApiInternalWire,
  ApiInvalidRequestWire,
  ApiServiceUnavailableWire,
  ApiUnauthorizedWire,
  definePublicApiError,
  publicApiWire,
} from "../../../public-api/domain/public-api-error"

// ---------------------------------------------------------------------------
// One wire class per BMS domain error
// ---------------------------------------------------------------------------

/** `BmsNotFoundError` on the wire. Also what a cross-org read returns. */
export class BmsApiNotFoundError extends definePublicApiError<BmsApiNotFoundError>()(
  "BmsApiNotFoundError",
  "not_found",
) {}
export const BmsApiNotFoundWire = publicApiWire(
  BmsApiNotFoundError,
  "BmsApiNotFoundError",
  "not_found",
)

/** `BmsVersionConflictError`. `details[]` carries the current version. */
export class BmsApiVersionConflictError extends definePublicApiError<BmsApiVersionConflictError>()(
  "BmsApiVersionConflictError",
  "conflict",
) {}
export const BmsApiVersionConflictWire = publicApiWire(
  BmsApiVersionConflictError,
  "BmsApiVersionConflictError",
  "conflict",
)

/** `BmsIdempotencyConflictError`. The same key, a different body. */
export class BmsApiIdempotencyConflictError extends definePublicApiError<BmsApiIdempotencyConflictError>()(
  "BmsApiIdempotencyConflictError",
  "idempotency_conflict",
) {}
export const BmsApiIdempotencyConflictWire = publicApiWire(
  BmsApiIdempotencyConflictError,
  "BmsApiIdempotencyConflictError",
  "idempotency_conflict",
)

/** `BmsValidationError`. `details[]` names every offending path. */
export class BmsApiValidationError extends definePublicApiError<BmsApiValidationError>()(
  "BmsApiValidationError",
  "invalid_request",
) {}
export const BmsApiValidationWire = publicApiWire(
  BmsApiValidationError,
  "BmsApiValidationError",
  "invalid_request",
)

/** `BmsRegionUnsetError`. 409: the firm's configuration is not ready. */
export class BmsApiRegionUnsetError extends definePublicApiError<BmsApiRegionUnsetError>()(
  "BmsApiRegionUnsetError",
  "conflict",
) {}
export const BmsApiRegionUnsetWire = publicApiWire(
  BmsApiRegionUnsetError,
  "BmsApiRegionUnsetError",
  "conflict",
)

/** `BmsForbiddenError`. Only where the caller already knows the record exists. */
export class BmsApiForbiddenError extends definePublicApiError<BmsApiForbiddenError>()(
  "BmsApiForbiddenError",
  "insufficient_scope",
) {}
export const BmsApiForbiddenWire = publicApiWire(
  BmsApiForbiddenError,
  "BmsApiForbiddenError",
  "insufficient_scope",
)

// ---------------------------------------------------------------------------
// The one list both halves are built from
// ---------------------------------------------------------------------------

/**
 * Every BMS-private wire error, tag beside schema. The single source both
 * `BMS_ENVELOPE_ERROR_TAGS` and `withBmsErrors` read, so the boundary's
 * pass-through list and the contract's declarations cannot drift.
 */
export const BMS_WIRE_ERRORS = [
  { tag: "BmsApiNotFoundError", wire: BmsApiNotFoundWire },
  { tag: "BmsApiVersionConflictError", wire: BmsApiVersionConflictWire },
  { tag: "BmsApiIdempotencyConflictError", wire: BmsApiIdempotencyConflictWire },
  { tag: "BmsApiValidationError", wire: BmsApiValidationWire },
  { tag: "BmsApiRegionUnsetError", wire: BmsApiRegionUnsetWire },
  { tag: "BmsApiForbiddenError", wire: BmsApiForbiddenWire },
] as const

/**
 * For `makePublicApiBoundaryLayer({ additionalEnvelopeErrorTags })`. Only the
 * BMS-private tags: the frozen public catalogue is already known to the
 * boundary through `PUBLIC_API_ERROR_TAGS`.
 */
export const BMS_ENVELOPE_ERROR_TAGS: ReadonlySet<string> = new Set(
  BMS_WIRE_ERRORS.map((entry) => entry.tag),
)

/**
 * Declare every BMS error on a group. Call it around the `.add(...)` chain,
 * before `.middleware(...)` and `.prefix(...)`:
 *
 *   export const AuditGroup = withBmsErrors(
 *     HttpApiGroup.make("audit").add(listAudit),
 *   ).middleware(BmsSessionMiddleware).prefix("/api/bms/audit")
 *
 * Written as an explicit chain rather than a fold over `BMS_WIRE_ERRORS`
 * because `.addError` widens the group's Error type parameter on every call,
 * and that widening is the compile-time proof that a handler failing with one
 * of these declared it. A fold would erase it, and the boundary would then
 * render an undeclared tag as an empty body at runtime with nothing failing at
 * build time. `bms-wire-errors.test.ts` asserts this chain and
 * `BMS_ENVELOPE_ERROR_TAGS` name the same set.
 *
 * The four shared public-catalogue errors go on too: the session and
 * credential failures the middleware raises, the strict-decode failure, and
 * the internal error a handler maps an unexpected repository failure onto.
 * Declared at GROUP level for the reason the calls lane gives, that one shared
 * error mapper forces the union onto every endpoint. Acceptable because a BMS
 * internal group's OpenAPI is not published.
 *
 * And the public lane's scope refusal. `requireBmsScope` fails with the
 * catalogue's `ApiInsufficientScopeError` and `mapBmsErrors` passes it through
 * as the 403 it already is, so the contract has to declare it or the handler
 * does not type-check. One contract serves both lanes, so the internal lane
 * declares a 403 it never raises; that is the price of one contract.
 */
export const withBmsErrors = <
  Id extends string,
  Endpoints extends HttpApiEndpoint.HttpApiEndpoint.Any,
  GroupError,
  R,
  TopLevel extends boolean,
>(
  group: HttpApiGroup.HttpApiGroup<Id, Endpoints, GroupError, R, TopLevel>,
) =>
  group
    .addError(BmsApiNotFoundWire)
    .addError(BmsApiVersionConflictWire)
    .addError(BmsApiIdempotencyConflictWire)
    .addError(BmsApiValidationWire)
    .addError(BmsApiRegionUnsetWire)
    .addError(BmsApiForbiddenWire)
    .addError(ApiInvalidRequestWire)
    .addError(ApiUnauthorizedWire)
    .addError(ApiServiceUnavailableWire)
    .addError(ApiInternalWire)
    .addError(ApiInsufficientScopeWire)
